'use client';

import * as React from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,

} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface DataTableProps<TData, TValue> {
  columns: Array<ColumnDef<TData, TValue>>
  data: TData[]
}

export function DriveOffTableComponent<TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,

    },
  });

  return (

    <div>
      <div className="sm:mb-0">
        <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold ">
          DriveOffs
        </h1>
      </div>
       <div className="flex items-center py-4">
        <Input
          placeholder="Filter locations..."
          value={(table.getColumn('location')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('location')?.setFilterValue(event.target.value)
          }
          className="text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-gray-100 dark:border-slate-700"
        />
      </div>
      <div className="text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-gray-100 dark:border-slate-700">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length
? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )
: (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4 ">
      <div className="flex w-[100px] text-slate-800 dark:text-slate-100 items-center justify-center text-sm font-medium">
          Page {table.getState().pagination.pageIndex + 1} of{' '}
          {table.getPageCount()}
      </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { table.previousPage(); }}
          disabled={!table.getCanPreviousPage()}
          className="text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-gray-100 dark:border-slate-700"
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { table.nextPage(); }}
          disabled={!table.getCanNextPage()}
          className="text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-gray-100 dark:border-slate-700"
        >
          Next
        </Button>
      </div>
    </div>
  );
}
